import ModalHeader from '@molecules/ModalHeader';
import ModalWindow from '@organisms/modals/ModalWindow';
import {ChangeEvent, FC, useEffect, useState} from 'react';
import {
  CheckboxListBlock,
  CheckboxListRow,
  ButtonsWrapper,
  ModalContent,
  ModalWrapper,
  GreyBlockWrapper,
} from './AddEditModuleModal.styles';
import Button from '@atoms/Button';
import Input from '@atoms/Input';
import Text from '@atoms/Text';
import {theme} from '@styles/theme';
import Checkbox from '@atoms/Checkbox';
import {useDispatch, useSelector} from 'react-redux';
import {tests} from '@store/slices/tests';
import {ITest, IModule} from '@types';
import {addModule, editModule} from '@store/slices/systems';
import {AppDispatch} from '@store';
import {toast} from "react-toastify";
import Toast from "@atoms/Toast";

interface Props {
  isOpened: boolean;
  onCloseRequest: () => void;
  onDelete: () => void;
  module?: IModule;
  subsystem_id: number;
}

const AddEditModuleModal: FC<Props> = ({
  isOpened,
  onCloseRequest,
  onDelete,
  module,
  subsystem_id,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const testsArr: ITest[] = useSelector(tests);
  const [moduleName, setModuleName] = useState<string>(module ? module.name : '');
  const [testTypesIds, setTestTypesIds] = useState<number[]>([]);
  const [defaultTestTypesIds, setDefaultTestTypesIds] = useState<number[]>([]);

  useEffect(() => {
    setModuleName(module?.name || '');
    if (module?.cells) {
      const selectedTestIds = module.cells.map(cell => cell.test_type_id);
      setTestTypesIds(selectedTestIds);
      setDefaultTestTypesIds(selectedTestIds);
    } else {
      setTestTypesIds([]); // Сбрасываем для нового модуля
    }
  }, [module]);

  const onSave = () => {
    if (module?.id) {
      const addedTestTypesIds = testTypesIds.filter(
        id => !defaultTestTypesIds.includes(id),
      );
      const deletedTestTypesIds = defaultTestTypesIds.filter(
        id => !testTypesIds.includes(id),
      );
      dispatch(
        editModule({
          module_id: module.id,
          name: moduleName,
          add_test_types_ids: addedTestTypesIds,
          delete_test_types_ids: deletedTestTypesIds,
        }),
      )
        .unwrap()
        .then(() => {
          toast.success(
            <Toast title="Готово!" text="Модуль успешно отредактирован" type="success" />,
          );
          setModuleName(module?.name || '');
          if (module?.cells) {
            const selectedTestIds = module.cells.map(cell => cell.test_type_id);
            setTestTypesIds(selectedTestIds);
            setDefaultTestTypesIds(selectedTestIds);
          } else {
            setTestTypesIds([]); // Сбрасываем для нового модуля
          }
          onCloseRequest();
        })
        .catch((err: any) => {
          toast.error(
            <Toast
              title="Ошибка"
              text={err?.message || 'Не удалось отредактировать модуль'}
              type="error"
            />,
          );
        });
    } else {
      dispatch(
        addModule({
          subsystem_id: subsystem_id,
          name: moduleName,
          test_types_ids: testTypesIds,
        }),
      )
        .unwrap()
        .then(() => {
          toast.success(
            <Toast title="Готово!" text="Модуль успешно добавлен" type="success" />,
          );
          onCloseRequest();
        })
        .catch((err: any) => {
          toast.error(
            <Toast
              title="Ошибка"
              text={err?.message || 'Не удалось добавить модуль'}
              type="error"
            />,
          );
        });
    }
  };

  const onSelectTestType = (e: ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    const testId = Number(e.target.value);
    if (isChecked) {
      setTestTypesIds(prev => [...prev, testId]);
    } else {
      setTestTypesIds(prev => prev.filter(id => id !== testId));
    }
  };

  return (
    <ModalWindow isOpen={isOpened} overflowIsVisible>
      <ModalWrapper>
        <ModalHeader
          title={module ? 'Редактировать модуль' : 'Добавить модуль'}
          onClose={onCloseRequest}
        />
        <ModalContent>
          <Input
            id="module-name"
            label="Название модуля"
            value={moduleName}
            onChange={e => {
              setModuleName(e.currentTarget.value);
            }}
            withoutErrorText
            hint="Максимум 50 символов"
            maxLength={50}
          />
          <GreyBlockWrapper>
            <Text
              text="Доступные типы тестов"
              type="captionB"
              color={theme.colors.basic01}
            />
            <CheckboxListBlock>
              {testsArr.map(test => {
                return (
                  <CheckboxListRow key={`checkbox-row-test-${test.id}`}>
                    <Checkbox
                      id={`checkbox-test-${test.id}`}
                      label={test.name}
                      value={String(test.id)}
                      checked={testTypesIds.includes(test.id)}
                      onChange={onSelectTestType}
                    />
                  </CheckboxListRow>
                );
              })}
            </CheckboxListBlock>
          </GreyBlockWrapper>
        </ModalContent>
        <ButtonsWrapper>
          <Button
            fullWidth
            title={module?.id ? `Сохранить` : `Добавить`}
            variant="filled"
            disabled={!moduleName}
            onClick={onSave}
          />
          <Button
            fullWidth
            title={module?.id ? `Удалить Модуль` : `Закрыть`}
            variant={`outlined`}
            redButton={!!module?.id}
            onClick={() => {
              if (module?.id) {
                onDelete();
              } else {
                onCloseRequest();
              }
              setModuleName('');
            }}
          />
        </ButtonsWrapper>
      </ModalWrapper>
    </ModalWindow>
  );
};

export default AddEditModuleModal;
